/*
 * <cart-extras>
 * Handles the "+" add buttons inside the cart-drawer Add Extras upsell.
 * Adds the variant, then refreshes the drawer sections (items, footer, bubbles)
 * and itself via the Section Rendering API — reusing the same flow as product-form.js.
 */
if (!customElements.get('cart-extras')) {
  customElements.define(
    'cart-extras',
    class CartExtras extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        // Refresh the offer list whenever the cart changes elsewhere (product page,
        // quantity change, remove) so purchased items drop out of the offers.
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'cart-extras') return; // our own adds already re-render
          this.refresh();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      refresh() {
        fetch(`${this.cartUrl}?section_id=cart-add-extras`)
          .then((response) => response.text())
          .then((html) => {
            this.innerHTML = this.getSectionInnerHTML(html, '.js-cart-extras-contents');
          })
          .catch((error) => console.error(error));
      }

      get cartDrawer() {
        return document.getElementById('cart-drawer');
      }

      // This theme's window.routes has no cart_url; derive it from cart_add_url
      // (preserves any locale prefix, e.g. /en-gb/cart).
      get cartUrl() {
        return routes.cart_add_url.replace(/\/add(\.js)?$/, '');
      }

      onClick(event) {
        const button = event.target.closest('.cart-extras__add[data-variant-id]');
        if (!button) return;
        event.preventDefault();
        this.addVariant(button);
      }

      addVariant(button) {
        if (button.classList.contains('loading')) return;
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');

        const sections = this.sectionsToRender();
        // The Section Rendering API allows max 5 sections per request; drop the
        // non-visual a11y live-region to stay within the limit.
        const sectionIds = sections
          .map((section) => section.id)
          .filter((id) => id !== 'cart-live-region-text')
          .slice(0, 5)
          .join(',');
        const config = fetchConfig();
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({ id: button.dataset.variantId, quantity: 1 });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error('cart-extras add failed:', response.description || response.message);
              return null;
            }
            // Re-render from a FRESH cart render. The sections returned inline by
            // /cart/add.js can be stale when an automatic discount recalculates
            // (e.g. the 20%-off-2nd-box), which left the checkout total pre-add.
            return fetch(`${this.cartUrl}?sections=${encodeURIComponent(sectionIds)}`).then((r) => r.json());
          })
          .then((freshSections) => {
            if (!freshSections) return;
            this.renderSections(freshSections, sections);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-extras',
              productVariantId: button.dataset.variantId,
            });
          })
          .catch((error) => console.error(error))
          .finally(() => {
            button.classList.remove('loading');
            button.removeAttribute('aria-disabled');
          });
      }

      sectionsToRender() {
        const drawer = this.cartDrawer;
        const sections = drawer ? [...drawer.getSectionsToRender()] : [];

        // Include this component so purchased items drop out of the offer list.
        if (!sections.some((section) => section.id === 'cart-add-extras')) {
          sections.push({
            id: 'cart-add-extras',
            section: 'cart-add-extras',
            selector: '.js-cart-extras-contents',
          });
        }

        return sections;
      }

      renderSections(responseSections, sections) {
        if (!responseSections) return;

        sections.forEach((section) => {
          const target = document.getElementById(section.id);
          const html = responseSections[section.id];
          if (!target || !html) return;
          target.innerHTML = this.getSectionInnerHTML(html, section.selector);
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : '';
      }
    }
  );
}
